import React, { useEffect, useRef, useState } from 'react';
import { Container } from '../ui/Container';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { getImagePath } from '../../Utils/Utilities';
import { MapPin } from 'lucide-react';
import { useBrand } from '../../Utils/BrandContext';

const TeamSection = () => {
  const isMobile = useIsMobile();
  const teamRef = useRef(null);
  const [isTeamVisible, setIsTeamVisible] = useState(false);
  const { brand: activeBrand } = useBrand();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTeamVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (teamRef.current) {
      observer.observe(teamRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const founders = [
    {
      name: 'Ekta Shah',
      title: 'Co-Founder & CEO',
      image: getImagePath('/Images/ektashah.jpg'),
      description: 'Ekta pursued a career in Computer Architecture at Qualcomm Inc. (San Diego) after studying at the University of Illinois at Urbana-Champaign (USA). Along with empowering Indian MSMEs with ERP, she also plays an active role in India\'s Startup ecosystem.',
      linkedIn: 'https://in.linkedin.com/in/ekta1651'
    },
    {
      name: 'Parth Shah',
      title: 'Co-Founder & Chief Evangelist',
      image: getImagePath('/Images/parthshah2.jpg'),
      description: 'Parth studied at the University of Texas at Austin (USA) before joining the Peach group to participate in our nation\'s growth story. While he has 15+ years experience in ERP software, he has also innovated solutions in Embedded Systems and e-Governance.',
      linkedIn: 'https://in.linkedin.com/in/parthshah123'
    }
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = [
    getImagePath('/Images/BiziPeach1.png'),
    getImagePath('/Images/BiziModi1.png'),
    getImagePath('/Images/BiziPeacock1.png'),
    getImagePath('/Images/BiziTeam1.png')
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  // Mobile Layout
  if (isMobile) {
    return (
      <section id="bzvzTeam" className="bg-white pb-12 md:py-16 lg:py-20 border-top">

        {/* Slideshow - OUTSIDE Container for full width */}
        <div className="w-full pb-5">
          <div className="relative w-full h-auto">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Team ${index + 1}`}
                className={`w-full h-auto transition-opacity duration-500 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute top-0 left-0'}`}
              />
            ))}
          </div>
        </div>

        <Container>
          <div className="text-center">
            {/* Main Header */}
            <h2 className="text-[30px] md:text-[40px] font-bold secttl mb-0">
              Team Biziverse
            </h2>

            {/* Description */}
            <div className="text-text-primary px-4 md:px-8 mt-5 leading-relaxed space-y-4 md:space-y-3 max-w-3xl mx-auto">
              <p className="text-[14px] md:text-lg text-text-primary">
                Biziverse Pvt. Ltd. is a govt-recognized startup based in Gandhinagar, Gujarat.
              </p>

              <p className="text-[14px] md:text-lg text-text-primary">
                It is the latest venture of the Peach Group, established in <b>1985</b> by US-returned entrepreneurs <b>Haren Shah</b> and <b>Mayoor Amin</b>.
              </p>

              <p className="text-[14px] md:text-lg text-text-primary">
                Powered by their experience and the energy of their next generation, Ekta & Parth, Biziverse aims to "make every small business a <span className="whitespace-nowrap">smart business"</span>.
              </p>
            </div>

            {/* Founders Section */}
            <div id="bzvzFounders" className="mt-12 mb-8">
              <h3 className="text-[30px] md:text-[40px] secttl font-bold text-primary">
                Our Founders
              </h3>
            </div>

            {/* Founder Cards */}
            <div className="mt-8 space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-8 max-w-md md:max-w-5xl mx-auto px-4">
              {founders.map((founder, index) => (
                <div
                  key={index}
                  className="bg-background-gray rounded p-4 text-center border border-border"
                >
                  <div className="pt-5 pb-2.5">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-[60%] max-w-[200px] mx-auto rounded"
                    />
                  </div>
                  <div className="py-2.5 px-2 leading-tight">
                    <div className="font-semibold text-base md:text-lg text-text-primary">
                      {founder.name}
                      <svg className="inline-block w-4 h-4 ml-1 text-[#0077B5]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </div>
                    <div className="text-[14px] md:text-lg text-text-primary font-semibold">
                      {founder.title}
                    </div>
                  </div>
                  <div className="text-[14px] md:text-lg text-text-primary leading-relaxed">
                    {founder.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Desktop Layout
  return (
    <section id="bzvzTeam" className="bg-background-light border-top">
      <div className="py-12 md:py-16 lg:py-20 px-[6%]">
        <Container variant="wide">

          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[40px] font-bold text-text-primary mb-3">
              Our Team
            </h2>
          </div>

          {/* Team Photo - centered */}
          <div
            ref={teamRef}
            className={`flex justify-center mb-12 transition-all duration-1000 ease-out transform ${isTeamVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
          >
            <img
              src={getImagePath("/Images/1944Team2.jpeg")}
              className="max-w-5xl rounded-[5px]"
              alt="Team"
            />
          </div>

          {/* Two-column section below photo */}
          <div className="flex justify-between items-start max-w-5xl mx-auto gap-20">

            {/* Left: About text */}
            <div className=" text-text-primary leading-[29px] space-y-5">
              <p className="text-[15px]">
                Biziverse Pvt. Ltd. is a govt-recognized startup based in{' '}
                <strong>Gandhinagar, Gujarat</strong>, and a part of{' '}
                <strong>Peach group (est. 1987)</strong>. It was founded by US-returned entrepreneurs{' '}
                <a href="https://in.linkedin.com/in/ekta1651" target="_blank" rel="noreferrer" className="underline font-semibold">
                  Ekta Shah
                  <svg className="inline-block w-3.5 h-3.5 ml-0.5 mb-0.5 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>{' '}
                and{' '}
                <a href="https://in.linkedin.com/in/parthshah123" target="_blank" rel="noreferrer" className="underline font-semibold">
                  Parth Shah
                  <svg className="inline-block w-3.5 h-3.5 ml-0.5 mb-0.5 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>{' '}
                with the mission to make <strong>ERP Easy</strong> &amp; <strong>Affordable for Every Indian Business</strong>.
              </p>

              <p className="text-[15px]">
                Our team is made of <strong>dynamic</strong> &amp; <strong>dedicated technocrats</strong>, and backed by <strong>reputed investors</strong> with <strong>global experience.</strong>
              </p>

              <p className="text-[15px]">
                You are most welcome to{" "}
                <a
                  href="https://google.com/maps?ll=23.242881,72.627436&z=16&t=m&hl=en&gl=IN&mapclient=embed&cid=9729504244199277308"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold"
                >
                  visit us
                </a>{" "}
                <MapPin className="inline w-4 h-4" /> at our green campus in Gandhinagar!
              </p>
            </div>

            {/* Right: Peach Group */}
            <div className="text-center flex flex-col items-center justify-center bg-white rounded-[10px] p-4 border border-border">
              <h3 className="text-[26px] font-bold text-text-primary mb-1">Peach Group</h3>
              <p className="text-text-secondary text-[14px] mb-6">Pioneer in Electronics, e-Governance and ERP.</p>
              {/* Single image containing all 8 product logos */}
              <img
                src={activeBrand === 'bz' ? getImagePath("/Images/peachGroup_qikerp.png") : getImagePath("/Images/peachGroup_bz.png")}
                alt="Peach Group Products"
                className="w-full min-w-[360px]"
              />
            </div>

          </div>
        </Container>
      </div>
    </section>
  );
};

export default TeamSection;